import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

interface MultiButtonModalProps {
	buttonAlignment?: 'center' | 'right';
	className?: string;
	headerText?: string;
	bodyText?: string;
	firstButtonText?: string;
	secondButtonText?: string;
	firstButtonAction?: () => void;
	secondButtonAction?: () => void;
	isOpen: boolean
	children?: React.ReactNode;
	customHeader?: React.ReactNode;
	buttonRowContent?: React.ReactNode;
	secondButtonIcon?: React.ReactNode;
	secondButtonClassName?: string;
	buttonAutoWidth?: boolean;
	firstButtonNoBackground?: boolean;
	secondButtonWarn?: boolean;
	secondButtonDisabled?: boolean;
}

export default function MultiButtonConfirmationModal(props: MultiButtonModalProps) {
	const {
		buttonAlignment = 'center',
		className,
		headerText = '',
		bodyText = '',
		firstButtonText,
		secondButtonText,
		firstButtonAction,
		secondButtonAction,
		isOpen,
		children,
		customHeader,
		buttonRowContent,
		secondButtonIcon,
		secondButtonClassName,
		buttonAutoWidth = false,
		firstButtonNoBackground = false,
		secondButtonWarn = false,
		secondButtonDisabled = false,
	} = props;

	const buttonRef = useRef<HTMLButtonElement>(null);
	const secondButtonContentRef = useRef<HTMLDivElement>(null);

	// A fairly concise way of animating an auto width change
	useEffect(() => {
		if (!buttonAutoWidth) {
			return;
		}

		const scrollWidth = secondButtonContentRef.current?.scrollWidth;

		if (buttonRef.current && scrollWidth) {
			const {
				paddingLeft,
				paddingRight,
			} = window.getComputedStyle(buttonRef.current);
			const padding = parseInt(paddingLeft, 10) + parseInt(paddingRight, 10) ?? 0;

			buttonRef.current.style.width = `${scrollWidth + padding}px`;
		}
	}, [secondButtonText]);

	if (!isOpen) {
		return (<></>);
	}

	return (
		<div
			className={classNames('info-modal confirmation-dialog', [className], {
				'custom-header': customHeader,
			})}
		>
			{customHeader || (
				<div className="margin-bottom-medium modal-header-container">
					<span className="text-white font-weight-bold font-size-16 modal-header-text">
						{headerText}
					</span>
				</div>
			)}
			<div className="margin-bottom-large modal-body-container">
				{!children && (
					<span className="text-gray modal-body-text">{bodyText}</span>
				)}
				{children}
			</div>
			<div className={classNames(
				'justify-content-end',
				'width-full',
				buttonAlignment === 'center'
					? 'modal-button-div'
					: 'modal-button-div-right',
			)}>
				{buttonRowContent && (
					<div className="button-row-content">{buttonRowContent}</div>
				)}
				{firstButtonText && firstButtonAction && (
					<button
						onClick={firstButtonAction}
						className={classNames(
							'round padding-button-medium modal-first-button',
							{ 'background-light': !firstButtonNoBackground },
						)}
					>
						<span className="text-gray modal-first-button-text">
							{firstButtonText}
						</span>
					</button>
				)}
				{secondButtonText && secondButtonAction && (
					<button
						onClick={secondButtonAction}
						disabled={secondButtonDisabled}
						className={classNames(
							'round padding-button-medium margin-left-small modal-second-button overflow-hidden',
							{ 'background-green': !secondButtonWarn },
							{ 'semantic-red-bg-400': secondButtonWarn },
							[secondButtonClassName],
						)}
						ref={buttonRef}
					>
						<div
							className="modal-second-button-container"
							ref={secondButtonContentRef}
						>
							{secondButtonIcon}
							<span className="text-dark modal-second-button-text">
								{secondButtonText}
							</span>
						</div>
					</button>
				)}
			</div>
		</div>
	);
}
